/**
 * 微信 OAuth2.0 授权模块
 * 支持企业微信和微信公众号两种授权方式
 */

const AUTHORIZE_URL = 'https://open.weixin.qq.com/connect/oauth2/authorize'

/**
 * 构造授权 URL 基础方法
 * @param {Object} params
 * @param {String} params.appId - 企业 ID 或公众号 appId
 * @param {String} params.redirectUri - 授权回调地址
 * @param {String} params.scope - 授权作用域
 * @param {String} [params.state] - 状态值
 * @param {String} [params.agentId] - 应用 agentId（snsapi_privateinfo 时必填）
 * @returns {String}
 */
function buildAuthorizeUrl({ appId, redirectUri, scope, state, agentId }) {
  const encodedUri = encodeURIComponent(redirectUri)
  let url = `${AUTHORIZE_URL}?appid=${appId}&redirect_uri=${encodedUri}&response_type=code&scope=${scope}`

  if (state) url += `&state=${state}`
  if (agentId) url += `&agentid=${agentId}`

  return url + '#wechat_redirect'
}

class WxOAuth {
  constructor(options = {}) {
    this.corpId = options.corpId || ''
    this.agentId = options.agentId || ''
    this.redirectUri = options.redirectUri || window.location.origin + window.location.pathname
    this.scope = options.scope || 'snsapi_userinfo'
    this.state = options.state || ''
  }

  // ========== URL 构造 ==========

  /**
   * 构造企业微信授权链接
   * @param {Object} [options]
   * @param {String} [options.corpId] - 企业 ID
   * @param {String} [options.redirectUri] - 回调地址
   * @param {String} [options.scope] - snsapi_base / snsapi_userinfo / snsapi_privateinfo
   * @param {String} [options.state] - 状态值
   * @param {String} [options.agentId] - 应用 agentId
   * @returns {String}
   */
  buildOAuthUrl(options = {}) {
    return buildAuthorizeUrl({
      appId: options.corpId || this.corpId,
      redirectUri: options.redirectUri || this.redirectUri,
      scope: options.scope || this.scope,
      state: options.state || 'snsapi_base',
      agentId: (options.scope || this.scope) === 'snsapi_privateinfo'
        ? (options.agentId || this.agentId)
        : undefined,
    })
  }


  /**
   * 构造微信公众号授权链接
   * @param {Object} [options]
   * @param {String} [options.appId] - 公众号 appId
   * @param {String} [options.redirectUri] - 回调地址
   * @param {String} [options.scope] - snsapi_base / snsapi_userinfo
   * @param {String} [options.state] - 状态值
   * @returns {String}
   */
  buildWechatOAuthUrl(options = {}) {
    return buildAuthorizeUrl({
      appId: options.appId || '',
      redirectUri: options.redirectUri || this.redirectUri,
      scope: options.scope || 'snsapi_userinfo',
      state: options.state || '',
    })
  }

  // ========== 页面跳转 ==========

  /** 跳转企业微信静默授权页 */
  redirectToBaseOAuth(options = {}) {
    window.location.href = this.buildOAuthUrl(options)
  }

  /** 跳转微信公众号授权页 */
  redirectToWechatOAuth(options = {}) {
    window.location.href = this.buildWechatOAuthUrl(options)
  }

  // ========== 回调解析 ==========

  /**
   * 从当前 URL 获取 code 和 state
   * @returns {{ code: String|null, state: String|null }}
   */
  getCodeFromUrl() {
    const params = new URLSearchParams(window.location.search)
    console.log("params", params.get('code'), params.get('state'))
    return { code: params.get('code'), state: params.get('state') }
  }

  /**
   * 当前 URL 是否为 OAuth 回调（携带 code 参数）
   * @returns {Boolean}
   */
  hasOAuthCallback() {
    return new URLSearchParams(window.location.search).has('code')
  }

  /**
   * 清除 URL 中的 code 和 state 参数（不刷新页面）
   */
  clearCodeFromUrl() {
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('state')
    window.location.href = url.toString()
    window.history.replaceState({}, '', url.toString())
  }
}

// 导出单例
export default new WxOAuth()

// 导出类，用于创建自定义实例
export { WxOAuth }
